import { API_BASE_URL, getAuthToken } from "./teleConsultationApi";

/**
 * WebRTC + WebSocket signaling helper.
 *
 * Used by both Technician and Doctor teleconsultation pages.
 *
 * Protocol:
 *
 * {
 *   type: "join",
 *   consultation_id,
 *   participant_type
 * }
 *
 * {
 *   type: "offer",
 *   consultation_id,
 *   participant_type,
 *   offer
 * }
 *
 * {
 *   type: "answer",
 *   consultation_id,
 *   participant_type,
 *   answer
 * }
 *
 * {
 *   type: "ice-candidate",
 *   consultation_id,
 *   participant_type,
 *   candidate
 * }
 *
 * {
 *   type: "leave",
 *   consultation_id,
 *   participant_type
 * }
 */

// ---------------------------------------------------------
// ICE SERVERS
// ---------------------------------------------------------
//
// Keep STUN only for now.
// Add TURN later when the WebRTC connection is confirmed
// to be failing because of NAT/firewall restrictions.
//

export const ICE_SERVERS = [
  {
    urls: "stun:stun.l.google.com:19302",
  },
];

// ---------------------------------------------------------
// WEBSOCKET CONFIG
// ---------------------------------------------------------
//
// CRA environment variables must start with REACT_APP_.
//
// REACT_APP_WS_AUTH_MODE=none
// REACT_APP_WS_AUTH_MODE=query
//

const WS_AUTH_MODE =
  process.env.REACT_APP_WS_AUTH_MODE || "none";

console.log("[WebSocket Config]", {
  baseUrl: process.env.REACT_APP_WS_BASE_URL,
  authMode: process.env.REACT_APP_WS_AUTH_MODE,
  hasToken: !!getAuthToken(),
});

const MAX_RECONNECT_ATTEMPTS = 5;

// ---------------------------------------------------------
// WEBSOCKET URL
// ---------------------------------------------------------

export const getWebSocketUrl = (consultationId) => {
  const override = process.env.REACT_APP_WS_BASE_URL;

  let origin;

  if (override) {
    // Remove trailing "/" characters.
    origin = override.replace(/\/+$/, "");
  } else {
    const url = new URL(API_BASE_URL);

    const protocol =
      url.protocol === "https:" ? "wss:" : "ws:";

    origin = `${protocol}//${url.host}`;
  }

  let wsUrl =
    `${origin}/ws/teleconsultations/` +
    `${encodeURIComponent(consultationId)}/`;

  if (WS_AUTH_MODE === "query") {
    const token = getAuthToken();

    if (token) {
      wsUrl += `?token=${encodeURIComponent(token)}`;
    }
  }

  return wsUrl;
};

const redact = (url) => url.split("?")[0];

// ---------------------------------------------------------
// RECORDING HELPERS
// ---------------------------------------------------------

export const pickRecorderMimeType = () => {
  if (typeof MediaRecorder === "undefined") {
    return "";
  }

  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];

  return (
    candidates.find((type) =>
      MediaRecorder.isTypeSupported?.(type)
    ) || ""
  );
};

/**
 * Builds one MediaStream for MediaRecorder:
 *
 * remote video
 * fallback: local video
 *
 * plus mixed local + remote audio.
 */
export const createRecordingStream = (
  localStream,
  remoteStream
) => {
  const output = new MediaStream();

  let audioContext = null;

  const videoTrack =
    remoteStream?.getVideoTracks()[0] ||
    localStream?.getVideoTracks()[0];

  if (videoTrack) {
    output.addTrack(videoTrack);
  }

  const AudioContextClass =
    window.AudioContext ||
    window.webkitAudioContext;

  const sources = [
    localStream,
    remoteStream,
  ].filter(
    (stream) =>
      stream &&
      stream.getAudioTracks().length > 0
  );

  if (AudioContextClass && sources.length > 0) {
    audioContext = new AudioContextClass();

    const destination =
      audioContext.createMediaStreamDestination();

    sources.forEach((stream) => {
      const source =
        audioContext.createMediaStreamSource(
          new MediaStream(stream.getAudioTracks())
        );

      source.connect(destination);
    });

    destination.stream
      .getAudioTracks()
      .forEach((track) => {
        output.addTrack(track);
      });
  }

  return {
    stream: output,

    cleanup: () => {
      if (
        audioContext &&
        audioContext.state !== "closed"
      ) {
        audioContext.close().catch(() => {});
      }
    },
  };
};

// =========================================================
// TELE RTC CLIENT
// =========================================================

export class TeleRTCClient {
  constructor({
    consultationId,
    participantType = "TECHNICIAN",
    polite = true,
    localStream,
    shouldReconnect = () => true,
    callbacks = {},
  }) {
    this.consultationId = consultationId;

    this.participantType = participantType;

    this.polite = polite;

    this.localStream = localStream;

    this.shouldReconnect = shouldReconnect;

    this.cb = callbacks;

    this.pc = null;

    this.ws = null;

    this.remoteStream = null;

    this.started = false;

    this.closed = false;

    this.remoteLeft = false;

    // Perfect-negotiation state.
    this.makingOffer = false;
    this.ignoreOffer = false;
    this.settingRemoteAnswer = false;

    // Incoming ICE candidates that arrive before
    // remoteDescription is available.
    this.pendingCandidates = [];

    // Outgoing signaling messages that are generated
    // before WebSocket becomes OPEN.
    this.pendingSignals = [];

    this.reconnectTimer = null;

    this.reconnectAttempts = 0;
  }

  // =======================================================
  // START
  // =======================================================

  start() {
    if (this.started || this.closed) {
      return;
    }

    this.started = true;

    this.createPeerConnection();

    this.connectSocket();
  }

  // =======================================================
  // PEER CONNECTION
  // =======================================================

  createPeerConnection() {
    const pc = new RTCPeerConnection({
      iceServers: ICE_SERVERS,
    });

    this.pc = pc;

    this.remoteStream = new MediaStream();

    this.pendingCandidates = [];

    this.makingOffer = false;

    this.ignoreOffer = false;

    this.settingRemoteAnswer = false;

    console.log(
      "[WebRTC] Peer connection created"
    );

    // -----------------------------------------------------
    // LOCAL TRACKS
    // -----------------------------------------------------

    const tracks = this.localStream
      ? this.localStream.getTracks()
      : [];

    tracks.forEach((track) => {
      pc.addTrack(track, this.localStream);
    });

    // Make sure remote media can still be received
    // if local camera/microphone is unavailable.

    if (
      !tracks.some(
        (track) => track.kind === "video"
      )
    ) {
      pc.addTransceiver("video", {
        direction: "recvonly",
      });
    }

    if (
      !tracks.some(
        (track) => track.kind === "audio"
      )
    ) {
      pc.addTransceiver("audio", {
        direction: "recvonly",
      });
    }

    // -----------------------------------------------------
    // ICE CANDIDATES
    // -----------------------------------------------------

    pc.onicecandidate = (event) => {
      if (!event.candidate) {
        console.log(
          "[WebRTC] ICE gathering completed"
        );

        return;
      }

      console.log(
        "[WebRTC] Sending ICE candidate:",
        {
          candidate:
            event.candidate.candidate,

          sdpMid:
            event.candidate.sdpMid,

          sdpMLineIndex:
            event.candidate.sdpMLineIndex,
        }
      );

      const sent = this.send({
        type: "ice-candidate",

        consultation_id:
          this.consultationId,

        participant_type:
          this.participantType,

        candidate:
          event.candidate.toJSON
            ? event.candidate.toJSON()
            : event.candidate,
      });

      console.log(
        "[WebRTC] ICE candidate sent:",
        sent
      );
    };

    // -----------------------------------------------------
    // REMOTE TRACK
    // -----------------------------------------------------

    pc.ontrack = (event) => {
      console.log(
        "[WebRTC] Remote track received:",
        event.track.kind,
        event.track.id
      );

      if (!this.remoteStream) {
        this.remoteStream =
          new MediaStream();
      }

      const alreadyAdded =
        this.remoteStream
          .getTracks()
          .some(
            (track) =>
              track.id === event.track.id
          );

      if (!alreadyAdded) {
        this.remoteStream.addTrack(
          event.track
        );
      }

      console.log(
        "[WebRTC] Remote stream tracks:",
        {
          audio:
            this.remoteStream
              .getAudioTracks()
              .length,

          video:
            this.remoteStream
              .getVideoTracks()
              .length,
        }
      );

      this.remoteLeft = false;

      this.cb.onRemoteStream?.(
        this.remoteStream
      );
    };

    // -----------------------------------------------------
    // NEGOTIATION NEEDED
    // -----------------------------------------------------

    pc.onnegotiationneeded = () => {
      console.log(
        "[WebRTC] negotiationneeded"
      );

      this.negotiate();
    };

    // -----------------------------------------------------
    // SIGNALING STATE
    // -----------------------------------------------------

    pc.onsignalingstatechange = () => {
      console.log(
        "[WebRTC] Signaling state:",
        pc.signalingState
      );
    };

    // -----------------------------------------------------
    // CONNECTION STATE
    // -----------------------------------------------------

    pc.onconnectionstatechange = () => {
      console.log(
        "[WebRTC] Connection state:",
        pc.connectionState
      );

      if (pc !== this.pc) {
        return;
      }

      this.cb.onRtcState?.(
        pc.connectionState
      );

      if (
        pc.connectionState === "failed" &&
        !this.closed
      ) {
        console.log(
          "[WebRTC] Connection failed. Restarting ICE..."
        );

        try {
          pc.restartIce();
        } catch (error) {
          console.error(
            "[WebRTC] restartIce failed",
            error
          );
        }
      }
    };

    // -----------------------------------------------------
    // ICE CONNECTION STATE
    // -----------------------------------------------------

    pc.oniceconnectionstatechange = () => {
      console.log(
        "[WebRTC] ICE connection state:",
        pc.iceConnectionState
      );

      if (
        pc === this.pc &&
        pc.iceConnectionState === "failed" &&
        !this.closed
      ) {
        console.log(
          "[WebRTC] ICE failed. Restarting ICE..."
        );

        try {
          pc.restartIce();
        } catch (error) {
          console.error(
            "[WebRTC] restartIce failed",
            error
          );
        }
      }
    };
  }

  // =======================================================
  // DESTROY PEER CONNECTION
  // =======================================================

  destroyPeerConnection() {
    const pc = this.pc;

    if (!pc) {
      return;
    }

    pc.onicecandidate = null;

    pc.ontrack = null;

    pc.onnegotiationneeded = null;

    pc.onsignalingstatechange = null;

    pc.onconnectionstatechange = null;

    pc.oniceconnectionstatechange = null;

    try {
      pc.close();
    } catch (error) {
      console.error(
        "[WebRTC] close failed",
        error
      );
    }

    this.pc = null;
  }

  // =======================================================
  // RESET PEER CONNECTION
  // =======================================================

  resetPeerConnection() {
    console.log(
      "[WebRTC] Resetting peer connection"
    );

    this.destroyPeerConnection();

    // Reset negotiation state.
    this.pendingCandidates = [];

    this.makingOffer = false;

    this.ignoreOffer = false;

    this.settingRemoteAnswer = false;

    this.cb.onRemoteStream?.(null);

    this.cb.onRtcState?.("new");

    if (!this.closed) {
      this.createPeerConnection();
    }
  }

  // =======================================================
  // NEGOTIATE
  // =======================================================

  async negotiate() {
    const pc = this.pc;

    if (
      !pc ||
      this.closed ||
      pc.signalingState !== "stable"
    ) {
      return;
    }

    // Don't start another offer while one is already
    // being created.
    if (this.makingOffer) {
      console.log(
        "[WebRTC] Negotiation already in progress"
      );

      return;
    }

    try {
      this.makingOffer = true;

      console.log(
        "[WebRTC] Creating offer"
      );

      const offer =
        await pc.createOffer();

      if (
        pc !== this.pc ||
        pc.signalingState !== "stable"
      ) {
        return;
      }

      await pc.setLocalDescription(
        offer
      );

      console.log(
        "[WebRTC] Sending offer",
        pc.localDescription?.type
      );

      this.send({
        type: "offer",

        consultation_id:
          this.consultationId,

        participant_type:
          this.participantType,

        offer:
          pc.localDescription.toJSON
            ? pc.localDescription.toJSON()
            : pc.localDescription,
      });
    } catch (error) {
      console.error(
        "[WebRTC] Negotiation failed",
        error
      );
    } finally {
      this.makingOffer = false;
    }
  }

  // =======================================================
  // FLUSH INCOMING ICE
  // =======================================================

  async flushPendingCandidates() {
    const pc = this.pc;

    if (
      !pc ||
      !pc.remoteDescription
    ) {
      return;
    }

    const queued =
      this.pendingCandidates;

    this.pendingCandidates = [];

    console.log(
      "[WebRTC] Flushing queued ICE candidates:",
      queued.length
    );

    for (const candidate of queued) {
      try {
        await pc.addIceCandidate(
          candidate
        );
      } catch (error) {
        if (!this.ignoreOffer) {
          console.error(
            "[WebRTC] addIceCandidate failed",
            error
          );
        }
      }
    }
  }

  // =======================================================
  // HANDLE SIGNAL
  // =======================================================

  async handleSignal(raw) {
    // Be defensive: accept payloads nested under
    // data or message.

    const nested =
      (raw &&
        typeof raw.data === "object" &&
        raw.data) ||
      (raw &&
        typeof raw.message === "object" &&
        raw.message) ||
      {};

    const msg = {
      ...raw,
      ...nested,
    };

    const type = msg.type;

    if (!type) {
      return;
    }

    // Ignore messages that we originally sent because
    // the backend broadcasts them to the entire group.

    if (
      msg.participant_type &&
      msg.participant_type ===
        this.participantType
    ) {
      return;
    }

    if (!this.pc && !this.closed) {
      this.createPeerConnection();
    }

    const pc = this.pc;

    if (!pc) {
      return;
    }

    try {
      // ---------------------------------------------------
      // JOIN
      // ---------------------------------------------------

      if (type === "join") {
        console.log(
          "[WebRTC] Remote participant joined:",
          msg.participant_type
        );

        const needsFreshPeer =
          this.remoteLeft ||
          [
            "disconnected",
            "failed",
            "closed",
          ].includes(
            pc.connectionState
          );

        if (needsFreshPeer) {
          this.remoteLeft = false;

          this.resetPeerConnection();
        } else {
          await this.negotiate();
        }

        return;
      }

      // ---------------------------------------------------
      // LEAVE
      // ---------------------------------------------------

      if (type === "leave") {
        console.log(
          "[WebRTC] Remote participant left:",
          msg.participant_type
        );

        this.remoteLeft = true;

        this.cb.onRemoteStream?.(
          null
        );

        this.cb.onRemoteLeft?.();

        return;
      }

      // ---------------------------------------------------
      // OFFER / ANSWER
      // ---------------------------------------------------

      if (
        type === "offer" ||
        type === "answer"
      ) {
        console.log(
          "[WebRTC] Received",
          type,
          "from",
          msg.participant_type
        );

        let description =
          msg.offer ||
          msg.answer ||
          msg.sdp ||
          msg.description ||
          null;

        if (!description) {
          console.warn(
            "[WebRTC] No SDP description found",
            type
          );

          return;
        }

        // If backend sends SDP as a string.
        if (
          typeof description ===
          "string"
        ) {
          description = {
            type,
            sdp: description,
          };
        }

        // -------------------------------------------------
        // PERFECT NEGOTIATION
        // -------------------------------------------------

        const offerCollision =
          type === "offer" &&
          (
            this.makingOffer ||
            pc.signalingState !==
              "stable" ||
            this.settingRemoteAnswer
          );

        this.ignoreOffer =
          !this.polite &&
          offerCollision;

        if (this.ignoreOffer) {
          console.log(
            "[WebRTC] Ignoring colliding offer (impolite peer)"
          );

          return;
        }

        this.remoteLeft = false;

        this.settingRemoteAnswer =
          type === "answer";

        console.log(
          "[WebRTC] Setting remote description:",
          type
        );

        await pc.setRemoteDescription(
          description
        );

        this.settingRemoteAnswer = false;

        console.log(
          "[WebRTC] Remote description set:",
          pc.remoteDescription?.type
        );

        // Add ICE candidates that arrived before
        // remoteDescription was available.

        await this.flushPendingCandidates();

        // -------------------------------------------------
        // OFFER -> ANSWER
        // -------------------------------------------------

        if (type === "offer") {
          console.log(
            "[WebRTC] Creating answer"
          );

          const answer =
            await pc.createAnswer();

          await pc.setLocalDescription(
            answer
          );

          console.log(
            "[WebRTC] Sending answer"
          );

          this.send({
            type: "answer",

            consultation_id:
              this.consultationId,

            participant_type:
              this.participantType,

            answer:
              pc.localDescription.toJSON
                ? pc.localDescription.toJSON()
                : pc.localDescription,
          });
        }

        return;
      }

      // ---------------------------------------------------
      // ICE CANDIDATE
      // ---------------------------------------------------

      if (
        type === "ice-candidate"
      ) {
        const candidate =
          msg.candidate;

        if (!candidate) {
          return;
        }

        console.log(
          "[WebRTC] Received ICE candidate from",
          msg.participant_type
        );

        // Remote SDP has not arrived yet.
        // Queue candidate.

        if (!pc.remoteDescription) {
          console.log(
            "[WebRTC] Queueing ICE candidate until remote description exists"
          );

          this.pendingCandidates.push(
            candidate
          );

          return;
        }

        try {
          await pc.addIceCandidate(
            candidate
          );

          console.log(
            "[WebRTC] Remote ICE candidate added"
          );
        } catch (error) {
          if (!this.ignoreOffer) {
            console.error(
              "[WebRTC] addIceCandidate failed",
              error
            );
          }
        }
      }
    } catch (error) {
      this.settingRemoteAnswer = false;

      console.error(
        "[WebRTC] Signal handling failed",
        type,
        error
      );
    }
  }

  // =======================================================
  // SEND SIGNAL
  // =======================================================

  send(payload) {
    const ws = this.ws;

    // WebSocket is not ready.
    // Queue the message instead of dropping it.

    if (
      !ws ||
      ws.readyState !== WebSocket.OPEN
    ) {
      console.log(
        "[WebSocket] Socket not ready, queuing:",
        payload?.type
      );

      this.pendingSignals.push(
        payload
      );

      return false;
    }

    try {
      console.log(
        "[WebSocket] Sending:",
        payload.type
      );

      ws.send(
        JSON.stringify(payload)
      );

      return true;
    } catch (error) {
      console.error(
        "[WebSocket] Send failed:",
        error
      );

      this.pendingSignals.push(
        payload
      );

      return false;
    }
  }

  // =======================================================
  // WEBSOCKET
  // =======================================================

  connectSocket() {
    if (this.closed) {
      return;
    }

    if (
      this.ws &&
      (
        this.ws.readyState ===
          WebSocket.OPEN ||
        this.ws.readyState ===
          WebSocket.CONNECTING
      )
    ) {
      return;
    }

    const url =
      getWebSocketUrl(
        this.consultationId
      );

    console.log(
      "[WebSocket] Connecting",
      redact(url)
    );

    let ws;

    try {
      ws = new WebSocket(url);
    } catch (error) {
      console.error(
        "[WebSocket] Unable to create socket",
        error
      );

      this.cb.onError?.(
        "Unable to open the signaling connection."
      );

      return;
    }

    this.ws = ws;

    // -----------------------------------------------------
    // SOCKET OPEN
    // -----------------------------------------------------

    ws.onopen = () => {
      if (ws !== this.ws) {
        return;
      }

      console.log(
        "[WebSocket] Connected"
      );

      this.reconnectAttempts = 0;

      this.cb.onWsState?.(true);

      this.cb.onReconnecting?.(
        false
      );

      // ---------------------------------------------------
      // JOIN FIRST
      // ---------------------------------------------------

      this.send({
        type: "join",

        consultation_id:
          this.consultationId,

        participant_type:
          this.participantType,
      });

      // ---------------------------------------------------
      // FLUSH QUEUED SIGNALS
      // ---------------------------------------------------

      let hadQueuedOffer = false;

      if (
        this.pendingSignals.length > 0
      ) {
        console.log(
          "[WebSocket] Flushing queued signals:",
          this.pendingSignals.length
        );

        const queuedSignals = [
          ...this.pendingSignals,
        ];

        this.pendingSignals = [];

        hadQueuedOffer =
          queuedSignals.some(
            (payload) =>
              payload?.type ===
              "offer"
          );

        queuedSignals.forEach(
          (payload) => {
            this.send(payload);
          }
        );
      }

      // If an offer was already queued while
      // WebSocket was connecting, do not create
      // another offer.

      if (!hadQueuedOffer) {
        this.negotiate();
      }
    };

    // -----------------------------------------------------
    // SOCKET MESSAGE
    // -----------------------------------------------------

    ws.onmessage = (event) => {
      if (ws !== this.ws) {
        return;
      }

      let data;

      try {
        data = JSON.parse(
          event.data
        );
      } catch (error) {
        console.error(
          "[WebSocket] Invalid JSON message",
          error
        );

        return;
      }

      console.log(
        "[WebSocket] Message received:",
        data?.type,
        "from:",
        data?.participant_type
      );

      this.handleSignal(data);
    };

    // -----------------------------------------------------
    // SOCKET ERROR
    // -----------------------------------------------------

    ws.onerror = (event) => {
      console.error(
        "[WebSocket] Error",
        event
      );
    };

    // -----------------------------------------------------
    // SOCKET CLOSE
    // -----------------------------------------------------

    ws.onclose = (event) => {
      console.log(
        "[WebSocket] Closed",
        event.code
      );

      if (ws !== this.ws) {
        return;
      }

      this.cb.onWsState?.(
        false
      );

      if (this.closed) {
        return;
      }

      // Auth/policy rejection.
      // Retrying will not help.

      if (
        [
          1008,
          4001,
          4003,
          4401,
          4403,
        ].includes(event.code)
      ) {
        this.cb.onReconnecting?.(
          false
        );

        this.cb.onError?.(
          "The signaling connection was rejected by the server. Please log in again or contact support."
        );

        return;
      }

      this.scheduleReconnect();
    };
  }

  // =======================================================
  // RECONNECT
  // =======================================================

  scheduleReconnect() {
    if (
      this.closed ||
      this.reconnectTimer
    ) {
      return;
    }

    if (
      !this.shouldReconnect() ||
      this.reconnectAttempts >=
        MAX_RECONNECT_ATTEMPTS
    ) {
      this.cb.onReconnecting?.(
        false
      );

      if (this.shouldReconnect()) {
        this.cb.onError?.(
          "Unable to restore the connection. Please leave and join the meeting again."
        );
      }

      return;
    }

    this.reconnectAttempts += 1;

    this.cb.onReconnecting?.(
      true
    );

    const delay = Math.min(
      2000 *
        this.reconnectAttempts,
      10000
    );

    console.log(
      `[WebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`
    );

    this.reconnectTimer =
      setTimeout(() => {
        this.reconnectTimer = null;

        this.connectSocket();
      }, delay);
  }

  // =======================================================
  // CLOSE
  // =======================================================

  close(sendLeave = true) {
    if (this.closed) {
      return;
    }

    // Send leave before marking closed.
    if (sendLeave) {
      this.send({
        type: "leave",

        consultation_id:
          this.consultationId,

        participant_type:
          this.participantType,
      });
    }

    this.closed = true;

    if (this.reconnectTimer) {
      clearTimeout(
        this.reconnectTimer
      );

      this.reconnectTimer = null;
    }

    const ws = this.ws;

    this.ws = null;

    if (ws) {
      ws.onopen = null;

      ws.onmessage = null;

      ws.onerror = null;

      ws.onclose = null;

      try {
        ws.close(
          1000,
          "client-leave"
        );
      } catch (error) {
        console.error(
          "[WebSocket] close failed",
          error
        );
      }
    }

    this.destroyPeerConnection();

    this.remoteStream = null;

    this.pendingCandidates = [];

    this.pendingSignals = [];
  }
}